/**
 * =============================================================================
 * PHS COMMAND LAYOUT - Enterprise FM Operating System Shell
 * =============================================================================
 * Unified operational shell for all PHS workspaces.
 * Dark sidebar, premium top bar, global command bar (Cmd+K).
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import PHSCommandSidebar from './PHSCommandSidebar';
import PHSTopBar from './PHSTopBar';
import GlobalCommandBar, { useCommandBar } from './GlobalCommandBar';
import { Menu, X, Command } from 'lucide-react';

// Design tokens matching enterprise reference
export const TOKENS = {
  colors: {
    sidebarBg: '#071A1F',
    sidebarBgAlt: '#082621',
    primaryTeal: '#0F9F82',
    darkTeal: '#0F766E',
    softTeal: '#E6F7F3',
    pageBg: '#F6F8FA',
    cardBg: '#FFFFFF',
    border: '#E5E7EB',
    textPrimary: '#0F172A',
    textMuted: '#64748B',
    red: '#DC2626',
    amber: '#F59E0B',
    blue: '#2563EB',
    green: '#059669',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
  },
  radius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    '2xl': '1.5rem',
  }
};

export default function PHSCommandLayout({ 
  children, 
  user,
  onLogout,
  jobCounts = {},
  notifications = []
}) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  // Global Command Bar state
  const { isOpen: commandBarOpen, setIsOpen: setCommandBarOpen, open: openCommandBar, close: closeCommandBar } = useCommandBar();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1200) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div 
      className="min-h-screen flex"
      style={{ backgroundColor: TOKENS.colors.pageBg }}
    >
      {/* Desktop Sidebar - Fixed position */}
      <aside 
        className={`hidden lg:flex flex-col fixed left-0 top-0 bottom-0 z-40 transition-all duration-300 ${
          sidebarCollapsed ? 'w-16' : 'w-64'
        }`}
        style={{ backgroundColor: TOKENS.colors.sidebarBg }}
      >
        <PHSCommandSidebar 
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          jobCounts={jobCounts}
          user={user}
        />
      </aside>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black z-50"
              onClick={() => setMobileMenuOpen(false)}
            />
            {/* Sidebar */}
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-72 z-50 overflow-hidden"
              style={{ backgroundColor: TOKENS.colors.sidebarBg }}
            >
              <PHSCommandSidebar 
                collapsed={false}
                onToggle={() => {}}
                jobCounts={jobCounts}
                user={user}
                isMobile={true}
                onClose={() => setMobileMenuOpen(false)}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content Area */}
      <div 
        className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        }`}
      >
        {/* Top Bar */}
        <PHSTopBar 
          user={user}
          onLogout={onLogout}
          onMenuClick={() => setMobileMenuOpen(true)}
          notifications={notifications}
          onOpenCommandBar={openCommandBar}
        />

        {/* Page Content */}
        <main className="flex-1 overflow-x-hidden">
          {children}
        </main>
      </div>

      {/* Mobile Menu Toggle - Floating */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="lg:hidden fixed bottom-6 left-6 z-40 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105 active:scale-95"
        style={{ backgroundColor: TOKENS.colors.primaryTeal }}
        data-testid="mobile-menu-toggle"
      >
        {mobileMenuOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <Menu className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Global Command Bar (Cmd+K) */}
      <GlobalCommandBar 
        isOpen={commandBarOpen} 
        onClose={closeCommandBar}
      />
    </div>
  );
}
